import { call, put } from "redux-saga/effects";

import actions from "@/actions";
import { parseCoordinateConversionRule } from "@/models";
import { Api, ConfigurationManager, Map } from "@/utils";

import generateDefaultLightGroups from "../generate-default-light-groups";
import loadFloorSpaceCatalogs from "../load-floor-space-catalogs";
import loadFloorspaceFile from "../load-floorspace-file";
import loadLightGroupSettings from "../load-light-group-settings";
import loadLightGroups from "../load-light-groups";
import loadLights from "../load-lights";
import loadMarkers from "../load-markers";
import loadSite from "../load-site";

/**
 * Loads all initial map requirements including floor space, lights, light groups, and settings.
 *
 * @yields Calls loaders and dispatches initial map data.
 */
function* loadRequirements() {
    try {
        yield put(
            actions.window.openLoading(
                "Hold tight. We're loading the map settings.",
            ),
        );

        const location = window.location;

        yield call(ConfigurationManager.setFloorSpace, location);

        const { floorSpaceId } = ConfigurationManager.values;

        const { floorSpace, luminaireTypes, luminaireSummary } = yield call(
            Api.get,
            `v1/floor-spaces/${floorSpaceId}/with-lightsmaps?imageSize=large`,
        );

        const { swapXY } = parseCoordinateConversionRule(
            floorSpace.coordinateConversionRule,
        );

        const mapInfo = floorSpace.floorSpaceMaps?.[0];

        const floorSpaceLengthRaw =
            mapInfo?.floorSpaceLength ?? floorSpace.floorSpaceLength;

        const floorSpaceLength = parseFloat(floorSpaceLengthRaw) || 0;

        const lightGroups = yield call(loadLightGroups, {
            swapXY,
            floorSpaceLength,
        });

        const site = yield call(loadSite, { siteId: floorSpace.site_ID });
        const { siteName, organization_ID: organizationId } = site;

        yield call(loadFloorspaceFile, { siteId: floorSpace.site_ID });

        let { result: defaultLightGroups } = yield call(
            Api.get,
            `v1/default-control-groups/organization-id/${organizationId}`,
        );

        if (defaultLightGroups.length === 0) {
            const response = yield call(generateDefaultLightGroups, {
                organizationId,
            });
            defaultLightGroups = response.result;
        }

        yield call(loadLightGroupSettings, { lightGroups, defaultLightGroups });

        floorSpace.siteName = siteName;

        yield put(actions.map.general.updateLightTypes(luminaireTypes));

        const mapSettingsViewModel =
            Map.Converter.settings.APIToViewModel(floorSpace);
        const { spaceDetails } = mapSettingsViewModel;
        Map.setViewport({
            center: [
                Map.Converter.convertFeetToMeters(spaceDetails.height) / 2,
                Map.Converter.convertFeetToMeters(spaceDetails.width) / 2,
            ],
            zoom: undefined,
        });

        yield put(actions.map.general.setSettings(mapSettingsViewModel));

        yield call(loadLights, {
            luminaireSummary,
            positioningType:
                mapSettingsViewModel.fixtureDetails.positioningType,
        });

        yield call(loadMarkers, { floorSpaceId });
        yield call(loadFloorSpaceCatalogs, { floorSpaceId });
        yield put(actions.map.values.global.setInitHistory());

        yield put(
            actions.map.general.setDefaultLightGroups(defaultLightGroups),
        );
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log("error loading requirements: ", err);
    } finally {
        yield put(actions.window.closeLoading());
    }
}

export default loadRequirements;
